import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Search, TrendingUp, Filter } from "lucide-react";
import { toast } from "sonner";
import { WorkflowDetailModal } from "@/components/workflows/WorkflowDetailModal";
import { WorkflowLaunchWizard } from "@/components/workflows/WorkflowLaunchWizard";
import { MyWorkflowsTab } from "@/components/workflows/MyWorkflowsTab";
import { WorkflowInstanceDetailModal } from "@/components/workflows/WorkflowInstanceDetailModal";
import { getAvailableWorkflows, canAccessWorkflow, canAccessWorkflowSync, trackContentUsage } from "@/lib/workflowHelpers";
import { getUserTier } from "@/lib/tierUtils";
import { SectionExplainerVideo } from "@/components/common/SectionExplainerVideo";
import { buildResolutionPrefill } from "@/lib/governanceHealth";

import * as Icons from "lucide-react";

interface Workflow {
  id: string;
  workflow_key: string;
  title: string;
  description: string;
  category: string;
  estimated_time: string;
  difficulty: string;
  prerequisites: any;
  phases: any;
  icon_name: string;
  tier_restrictions: any;
  is_active: boolean;
  success_criteria: string[];
}

const WorkflowLibrary = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredWorkflows, setFilteredWorkflows] = useState<Workflow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<string>("available");
  const [highlightedWorkflowId, setHighlightedWorkflowId] = useState<string | null>(null);
  const [filterParam, setFilterParam] = useState<string | null>(null);
  const [launchWorkflow, setLaunchWorkflow] = useState<Workflow | null>(null);
  const [launchPrefill, setLaunchPrefill] = useState<Record<string, any> | undefined>(undefined);

  // React Query: Fetch workflows with caching (5 min stale time from QueryClient config)
  const { data: workflows = [], isLoading: loading } = useQuery({
    queryKey: ['workflows'],
    queryFn: getAvailableWorkflows,
  });

  // React Query: Fetch user tier with caching (10 min stale time)
  const { data: userTier = 'DIY' } = useQuery({
    queryKey: ['userTier'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 'DIY';
      return await getUserTier(user.id);
    },
    staleTime: 10 * 60 * 1000,
  });

  // React Query: Fetch workflow instances with caching (2 min stale time)
  const { data: workflowInstances = [], isLoading: loadingInstances, refetch: refetchInstances } = useQuery({
    queryKey: ['workflowInstances'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      console.log('[WorkflowLibrary] Fetching workflow instances...');

      // OPTIMIZED: Single query with JOINs to fetch all data at once
      const { data: tasks, error } = await supabase
        .from('custom_tasks')
        .select(`
          id,
          workflow_id,
          status,
          created_at,
          completed_at,
          workflows!inner(title, description, icon_name, phases),
          task_workflows(current_phase),
          task_phases(status),
          generated_documents(id),
          signature_requests(id)
        `)
        .eq('user_id', user.id)
        .eq('is_from_workflow', true)
        .not('workflow_id', 'is', null)
        .neq('status', 'abandoned')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error loading workflow instances:", error);
        throw error;
      }

      if (!tasks || tasks.length === 0) {
        console.log('[WorkflowLibrary] No workflow instances found');
        return [];
      }

      console.log(`[WorkflowLibrary] Found ${tasks.length} workflow instances`);

      // Transform data in memory (fast)
      return tasks.map((task: any) => {
        const phases = task.task_phases || [];
        const totalPhases = phases.length;
        const completedPhases = phases.filter((p: any) => p.status === 'completed').length;
        const progressPercentage = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0;

        return {
          id: task.id,
          workflow_id: task.workflow_id,
          task_id: task.id,
          workflow_title: task.workflows?.title || 'Unknown Workflow',
          workflow_description: task.workflows?.description || '',
          status: task.status,
          current_phase: task.task_workflows?.[0]?.current_phase || 1,
          total_phases: totalPhases,
          progress_percentage: progressPercentage,
          started_at: task.created_at,
          completed_at: task.completed_at,
          document_count: task.generated_documents?.length || 0,
          signature_count: task.signature_requests?.length || 0,
          icon_name: task.workflows?.icon_name || 'FileText',
        };
      });
    },
    staleTime: 30000, // 30 seconds (shorter for more frequent updates)
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Realtime subscription for workflow changes - managed at page level to prevent unmount/remount cycles
  useEffect(() => {
    console.log('[WorkflowLibrary] Setting up realtime subscription for workflows');
    
    const channel = supabase
      .channel('workflow-library-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'custom_tasks',
        },
        (payload) => {
          console.log('[WorkflowLibrary] Workflow change detected:', payload.eventType);
          
          // Invalidate to trigger refetch
          queryClient.invalidateQueries({ queryKey: ['workflowInstances'] });
          
          // Show toast for new workflows
          if (payload.eventType === 'INSERT' && payload.new?.is_from_workflow) {
            toast.success("New Workflow Started", {
              description: "Your workflow has been created successfully.",
            });
          }
        }
      )
      .subscribe();
    
    return () => {
      console.log('[WorkflowLibrary] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const categories = [
    { value: "all", label: "All Workflows" },
    { value: "Member Management", label: "Member Management" },
    { value: "Governance & Operations", label: "Governance & Operations" },
    { value: "Contributions & Finance", label: "Contributions & Finance" },
    { value: "Asset Transfers", label: "Asset Transfers" },
  ];

  const difficulties = [
    { value: "all", label: "All Levels" },
    { value: "Easy", label: "Easy" },
    { value: "Medium", label: "Medium" },
    { value: "Hard", label: "Hard" },
  ];


  // Track if URL params have been processed to prevent duplicate processing
  const urlParamsProcessedRef = useRef(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle URL query params - extract values once per searchParams change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const filterParam = searchParams.get('filter');
    const idParam = searchParams.get('id');
    const widParam = searchParams.get('wid');
    const launchedParam = searchParams.get('launched');
    
    // Update tab only if different
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    
    // Update search query and filter param only if different
    const newFilterParam = searchParams.get('filter');
    if (newFilterParam !== filterParam) {
      setFilterParam(newFilterParam);
      if (newFilterParam) {
        const query = newFilterParam.replace(/_/g, ' ');
        setSearchQuery(prev => prev !== query ? query : prev);
      }
    }
    
    // If ID param is provided, find and open that workflow
    if (idParam && workflows.length > 0) {
      const workflow = workflows.find(w => w.id === idParam);
      if (workflow && (!selectedWorkflow || selectedWorkflow.id !== workflow.id)) {
        setSelectedWorkflow(workflow);
      }
    }

    // Handle launched workflow highlight with proper cleanup
    if (launchedParam === '1' && widParam && highlightedWorkflowId !== widParam) {
      setHighlightedWorkflowId(widParam);
      
      // Clear any existing timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedWorkflowId(null);
        // Clear the launched param without triggering re-render loop
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('launched');
        setSearchParams(newParams, { replace: true });
      }, 3000);
    }
    
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, workflows.length]); // Use searchParams object directly, not .get() calls

  // Governance Health deep-link: auto-open the launch wizard with prefilled data
  const ghAutoLaunchRef = useRef<string | null>(null);
  useEffect(() => {
    const workflowKey = searchParams.get('workflow');
    const ghRule = searchParams.get('gh_rule');
    const ghItem = searchParams.get('gh_item');
    const ghPrefill = searchParams.get('gh_prefill');
    if (!workflowKey || workflows.length === 0) return;
    const dedupeKey = `${workflowKey}|${ghRule ?? ''}|${ghItem ?? ''}|${ghPrefill ?? ''}`;
    if (ghAutoLaunchRef.current === dedupeKey) return;

    const wf = workflows.find(w => w.workflow_key === workflowKey);
    if (!wf) return;
    ghAutoLaunchRef.current = dedupeKey;

    (async () => {
      let prefill: Record<string, any> | undefined = undefined;

      // Intent intake prefill (from GovernanceIntentIntake card)
      if (ghPrefill) {
        try {
          const raw = sessionStorage.getItem(`gh_intent_prefill_${ghPrefill}`);
          if (raw) {
            const seed = JSON.parse(raw);
            const { __suggested_title, __intent_text, __source, ...rest } = seed || {};
            prefill = {
              ...(rest || {}),
              ...(__suggested_title ? { title: __suggested_title, resolution_title: __suggested_title } : {}),
              ...(__intent_text ? { intent_summary: __intent_text } : {}),
            };
            sessionStorage.removeItem(`gh_intent_prefill_${ghPrefill}`);
          }
        } catch (e) {
          console.warn('gh_prefill parse failed', e);
        }
      }

      if (!prefill && ghRule) {
        const { data: rule } = await supabase
          .from('compliance_rule_catalog' as any)
          .select('rule_key, title, source_reference')
          .eq('rule_key', ghRule)
          .maybeSingle();
        if (rule) {
          prefill = buildResolutionPrefill(rule as any, ghItem);
        } else {
          prefill = { gh_rule: ghRule, ...(ghItem ? { gh_item: ghItem } : {}) };
        }
      }
      setLaunchPrefill(prefill);
      setLaunchWorkflow(wf as Workflow);
    })();
  }, [searchParams, workflows]);



  // Workflows to hide from Available Workflows display
  const hiddenWorkflowKeys = [
    'banking_authority_resolution',
    'transfer_asset_generic',
    'record_contribution'
  ];

  // Memoized filter function to prevent recreation on every render
  const filterWorkflows = useCallback(() => {
    // First, exclude hidden workflows
    let filtered = workflows.filter(w => !hiddenWorkflowKeys.includes(w.workflow_key));

    // Filter by search query with tokenized matching
    if (searchQuery) {
      const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      
      filtered = filtered.filter((w) => {
        const haystack = `${w.title} ${w.description ?? ''} ${w.workflow_key}`.toLowerCase();
        const matchesFilterParamVal = filterParam && w.workflow_key.toLowerCase().includes(filterParam.toLowerCase().replace(/_/g, ' '));
        const matchesTokens = tokens.every(token => haystack.includes(token));
        return matchesTokens || matchesFilterParamVal;
      });
    }

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((w) => w.category === selectedCategory);
    }

    // Filter by difficulty
    if (selectedDifficulty !== "all") {
      filtered = filtered.filter((w) => w.difficulty === selectedDifficulty);
    }

    // Filter by tier access using synchronous check
    const accessibleWorkflows = filtered.filter(workflow => 
      canAccessWorkflowSync(workflow, userTier)
    );
    
    setFilteredWorkflows(accessibleWorkflows);
  }, [workflows, searchQuery, selectedCategory, selectedDifficulty, userTier, filterParam]);

  // Run filter when dependencies change
  useEffect(() => {
    filterWorkflows();
  }, [filterWorkflows]);

  const handleDeleteWorkflow = async (workflowInstance: any) => {
    try {
      const { isValidUUID } = await import("@/lib/utils");
      
      if (!isValidUUID(workflowInstance.task_id)) {
        toast.error("Invalid workflow ID");
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to abandon a workflow");
        return;
      }

      // Use security definer function to bypass RLS recursion
      const { data: success, error } = await supabase.rpc('abandon_task', {
        p_task_id: workflowInstance.task_id,
        p_user_id: user.id
      });

      if (error) throw error;
      if (!success) throw new Error("Failed to abandon workflow - permission denied");

      toast.success("Workflow abandoned successfully");
      
      // Invalidate React Query cache to refetch workflow instances
      queryClient.invalidateQueries({ queryKey: ['workflowInstances'] });
    } catch (error) {
      console.error("Error abandoning workflow:", error);
      toast.error("Failed to abandon workflow");
    }
  };

  // filterWorkflows is now memoized with useCallback above

const handleWorkflowClick = (workflow: Workflow) => {
  setSelectedWorkflow(workflow);
  // Defensive: only track if ID is a valid UUID
  import("@/lib/utils").then(({ isValidUUID }) => {
    if (isValidUUID(workflow.id)) {
      trackContentUsage("workflow", workflow.id, "viewed");
    } else {
      console.warn("Invalid workflow id for analytics:", workflow.id);
    }
  });
};

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return "bg-green-500/10 text-green-500";
      case "Medium":
        return "bg-yellow-500/10 text-yellow-500";
      case "Hard":
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getIconComponent = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons] as any;
    return Icon ? <Icon className="h-6 w-6" /> : <Icons.FileText className="h-6 w-6" />;
  };

  if (loading) {
    return (
      <div className="container max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading workflows...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Section Explainer Video */}
      <SectionExplainerVideo
        videoUrl="https://vimeo.com/1152239316"
        sectionKey="workflows"
        title="Workflows Overview"
        description="Learn how to launch and track workflows for common UNA tasks"
      />

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold">Workflows</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Launch workflows and track your progress through common UNA tasks
        </p>
      </div>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="available">Available Workflows</TabsTrigger>
          <TabsTrigger value="my-workflows">My Workflows</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-6 mt-6">

          {/* Search and Filters */}
          <div className="flex flex-col gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-base"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 rounded-md border bg-background min-w-[44px] min-h-[44px] text-base"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className="px-4 py-2 rounded-md border bg-background min-w-[44px] min-h-[44px] text-base"
          >
            {difficulties.map((diff) => (
              <option key={diff.value} value={diff.value}>
                {diff.label}
              </option>
            ))}
          </select>
          </div>
          </div>

          {/* Workflow Cards - Mobile: 1 column, Tablet: 2, Desktop: 3 */}
          {filteredWorkflows.length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <p className="text-muted-foreground">No workflows found matching your criteria</p>
              {(searchQuery || selectedCategory !== "all" || selectedDifficulty !== "all") && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("all");
                    setSelectedDifficulty("all");
                  }}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredWorkflows.map((workflow) => (
                <Card
                  key={workflow.id}
                  className="hover:shadow-lg transition-all cursor-pointer active:scale-98"
                  onClick={() => handleWorkflowClick(workflow)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        {getIconComponent(workflow.icon_name)}
                      </div>
                      <Badge className={getDifficultyColor(workflow.difficulty)}>
                        {workflow.difficulty}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{workflow.title}</CardTitle>
                    <CardDescription className="line-clamp-2 text-sm">
                      {workflow.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{workflow.estimated_time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" />
                        <span>{Array.isArray(workflow.phases) ? workflow.phases.length : 0} phases</span>
                      </div>
                    </div>
                    {workflow.prerequisites && Array.isArray(workflow.prerequisites) && workflow.prerequisites.length > 0 && (
                      <div className="mt-3">
                        <Badge variant="outline" className="text-xs">
                          Prerequisites required
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my-workflows" className="mt-6">
          <MyWorkflowsTab
            workflows={workflowInstances}
            loading={loadingInstances}
            onOpenWorkflow={setSelectedInstance}
            onDeleteWorkflow={handleDeleteWorkflow}
            highlightedWorkflowId={highlightedWorkflowId}
          />
        </TabsContent>
      </Tabs>

      {/* Workflow Detail Modal */}
      {selectedWorkflow && (
        <WorkflowDetailModal
          workflow={selectedWorkflow}
          open={!!selectedWorkflow}
          onOpenChange={(open) => !open && setSelectedWorkflow(null)}
        />
      )}

      {/* Workflow Instance Detail Modal */}
      {selectedInstance && (
        <WorkflowInstanceDetailModal
          workflowInstance={selectedInstance}
          open={!!selectedInstance}
          onOpenChange={(open) => !open && setSelectedInstance(null)}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['workflowInstances'] })}
        />
      )}

      {/* Governance Health deep-link auto-launch */}
      {launchWorkflow && (
        <WorkflowLaunchWizard
          workflow={launchWorkflow}
          open={!!launchWorkflow}
          onOpenChange={(open) => {
            if (!open) {
              setLaunchWorkflow(null);
              setLaunchPrefill(undefined);
              // Clear deep-link params so it doesn't re-open on state changes
              const next = new URLSearchParams(searchParams);
              next.delete('workflow');
              next.delete('gh_rule');
              next.delete('gh_item');
              next.delete('gh_prefill');
              setSearchParams(next, { replace: true });
            }
          }}
          initialWorkflowData={launchPrefill}
        />
      )}
    </div>
  );
};

export default WorkflowLibrary;
